import { format } from 'date-fns';
import { isVisaExpired, isVisaExpiring } from '@cls/shared';

export interface BadgeWorker {
  id?: string;
  name?: string;
  core?: boolean | null;
  rating?: number | null;
  rtwStatus?: string | null;
  rtwExpiry?: string | null;
  nationality?: string | null;
}

export interface BadgeAvailabilitySignal {
  id: string;
  workerId: string;
  signaledAt: string;
}

export function ratingStars(w: BadgeWorker | null | undefined, size = 11): JSX.Element {
  if (!w || w.rating == null || w.rating === 0) return <span style={{ fontSize: size - 1, color: 'var(--faint)' }}>—</span>;
  return (
    <span style={{ color: '#F59E0B', fontSize: size, letterSpacing: 1 }}>
      {'★'.repeat(Math.round(w.rating))}
    </span>
  );
}

export function coreBadge(w: BadgeWorker | null | undefined, compact = false): JSX.Element | null {
  if (!w?.core) return null;
  return (
    <span style={{ fontSize: compact ? 8 : 9, padding: '1px 5px', borderRadius: 4, background: 'rgba(232,164,39,.18)', color: 'var(--accent)', fontWeight: 700 }}>
      CORE
    </span>
  );
}

export function rtwBadge(w: BadgeWorker | null | undefined): JSX.Element | null {
  if (!w?.rtwStatus) return null;
  const base = { fontSize: 9, padding: '1px 6px', borderRadius: 4, fontWeight: 700 } as const;

  if (w.rtwStatus === 'uk') {
    return (
      <span title="UK Citizen" style={{ ...base, background: 'rgba(34,197,94,.12)', color: '#22c55e', border: '1px solid rgba(34,197,94,.3)' }}>
        UK
      </span>
    );
  }
  if (w.rtwStatus === 'iltr') {
    return (
      <span title="Indefinite Leave to Remain" style={{ ...base, background: 'rgba(34,197,94,.12)', color: '#22c55e', border: '1px solid rgba(34,197,94,.3)' }}>
        ILTR
      </span>
    );
  }
  if (w.rtwStatus === 'visa') {
    let dateTxt = '';
    if (w.rtwExpiry) {
      const d = new Date(w.rtwExpiry);
      if (!Number.isNaN(d.getTime())) dateTxt = ' ' + format(d, 'd MMM yy');
    }
    if (isVisaExpired(w.rtwStatus, w.rtwExpiry)) {
      return (
        <span title={`Visa expired ${w.rtwExpiry || '(no date)'}`} style={{ ...base, background: 'rgba(239,68,68,.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,.35)' }}>
          {`VISA EXP${dateTxt}`}
        </span>
      );
    }
    if (isVisaExpiring(w.rtwStatus, w.rtwExpiry, 90)) {
      return (
        <span title={`Visa expiring ${w.rtwExpiry || '(no date)'}`} style={{ ...base, background: 'rgba(251,191,36,.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,.35)' }}>
          {`VISA*${dateTxt}`}
        </span>
      );
    }
    return (
      <span title={`Visa valid until ${w.rtwExpiry || '(no date)'}`} style={{ ...base, background: 'rgba(59,130,246,.12)', color: '#3b82f6', border: '1px solid rgba(59,130,246,.3)' }}>
        {`VISA${dateTxt}`}
      </span>
    );
  }
  return null;
}

export function natBadge(w: BadgeWorker | null | undefined): JSX.Element | null {
  if (!w?.nationality) return null;
  return (
    <span title={`Nationality: ${w.nationality}`} style={{ fontSize: 9, color: 'var(--muted)', padding: '1px 6px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 4 }}>
      {w.nationality}
    </span>
  );
}

function signalAge(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export function availBadge(w: BadgeWorker | null | undefined, signals: BadgeAvailabilitySignal[]): JSX.Element | null {
  if (!w?.id) return null;
  const s = signals.find((x) => x.workerId === w.id);
  if (!s) return null;
  return (
    <span className="badge" title={`Self-reported available ${signalAge(s.signaledAt)}`} style={{ background: 'rgba(34,197,94,.18)', color: '#22c55e', border: '1px solid rgba(34,197,94,.45)', fontSize: 9, fontWeight: 700, padding: '1px 7px' }}>
      🟢 AVAILABLE
    </span>
  );
}

export function initials(name: string): string {
  return name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
}

export function avatarColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360}, 60%, 55%)`;
}

export function avEl(workerOrName: { name?: string; photo?: string | null } | string, size = 34, font = 12): JSX.Element {
  const obj = typeof workerOrName === 'string' ? { name: workerOrName, photo: null as string | null } : workerOrName;
  const name = obj.name ?? '';
  if (obj.photo) {
    return (
      <div className="av av-photo" style={{ width: size, height: size }}>
        <img src={obj.photo} alt={name} loading="lazy" />
      </div>
    );
  }
  return (
    <div className="av" style={{ width: size, height: size, fontSize: font, background: avatarColor(name) }}>
      {initials(name)}
    </div>
  );
}
